import { reminderZoneFormatter, resolveReminderInstant } from "./reminder-local-time";
import { catalogueTypeIds, catalogueTypeLabels, classifyReminderType } from "./catalogue";

/** A private free-form reminder need not claim membership of the published catalogue. */
export const trackedRenewalTypes = [...catalogueTypeIds, "unclassified"] as const;
export type TrackedRenewalType = (typeof trackedRenewalTypes)[number];

export const intendedNotificationChannels = [
  "in-app",
  "email",
  "calendar",
  "push",
  "whatsapp",
  "sms",
] as const;
export type IntendedNotificationChannel = (typeof intendedNotificationChannels)[number];

export type ReminderCue = Readonly<{
  minutesBeforeEvent?: number;
  offsetDays: number;
  localTime: string;
}>;

export const trackedRenewalStates = ["active", "closed"] as const;
export type TrackedRenewalState = (typeof trackedRenewalStates)[number];

export const renewalClosureOutcomes = ["completed", "ended"] as const;
export type RenewalClosureOutcome = (typeof renewalClosureOutcomes)[number];

export const occurrenceAwarenessActions = ["acknowledge", "withdraw"] as const;
export type OccurrenceAwarenessAction = (typeof occurrenceAwarenessActions)[number];
export type OccurrenceAwarenessState = "aware" | "not-aware";

export type DueReminderCue = ReminderCue &
  Readonly<{
    dueAtEpochMilliseconds: number;
  }>;

export type TrackedRenewalPersonScope =
  Readonly<{ kind: "household" }> | Readonly<{ kind: "person-linked"; subjectAdultId: string }>;

export const personScopeApprovalStates = ["pending", "approved", "rejected", "withdrawn"] as const;
export type PersonScopeApprovalState = (typeof personScopeApprovalStates)[number];

export function normalizeTrackedRenewalPersonScope(
  value: TrackedRenewalPersonScope,
): TrackedRenewalPersonScope {
  if (value.kind === "household") return { kind: "household" };
  const subjectAdultId = value.subjectAdultId.trim();
  if (subjectAdultId.length < 8 || subjectAdultId.length > 120) {
    throw new Error("Choose an active household adult for person-linked privacy.");
  }
  return { kind: "person-linked", subjectAdultId };
}

export type TrackedRenewalDraft = Readonly<{
  type: TrackedRenewalType;
  title: string;
  authoritativeDate: string;
  timeZone: string;
  responsibleAdultId: string;
  recipientAdultIds: ReadonlyArray<string>;
  reminderCues: ReadonlyArray<ReminderCue>;
  intendedChannels: ReadonlyArray<IntendedNotificationChannel>;
  personScope: TrackedRenewalPersonScope;
  providerLabel?: string | null;
  assetLabel?: string | null;
}>;

export const trackedRenewalTypeLabels: Readonly<Record<TrackedRenewalType, string>> = {
  ...catalogueTypeLabels,
  unclassified: "Personal reminder",
};

export function classifyTrackedRenewalType(type: string) {
  return type === "unclassified"
    ? {
        type: "unclassified" as const,
        label: trackedRenewalTypeLabels.unclassified,
        categoryId: null,
        subcategoryId: null,
      }
    : classifyReminderType(type);
}

export function normalizeTrackedRenewalTitle(value: string): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (normalized.length < 2 || normalized.length > 120) {
    throw new Error("Enter a title between 2 and 120 characters.");
  }
  return normalized;
}

export function normalizeTrackedRenewalOptionalLabel(
  value: string | null | undefined,
): string | null {
  if (value === null || value === undefined) return null;
  const normalized = value.normalize("NFKC").trim().replace(/\s+/g, " ");
  if (normalized.length === 0) return null;
  if (normalized.length > 80) {
    throw new Error("Use a non-sensitive label of no more than 80 characters.");
  }
  return normalized;
}

export function trackedRenewalDuplicateDiscriminator(
  value: Readonly<{
    personScope: TrackedRenewalPersonScope;
    providerLabel: string | null | undefined;
    assetLabel: string | null | undefined;
  }>,
): string | null {
  const personScope = normalizeTrackedRenewalPersonScope(value.personScope);
  if (personScope.kind === "person-linked") return `person:${personScope.subjectAdultId}`;
  const assetLabel = normalizeTrackedRenewalOptionalLabel(value.assetLabel);
  if (assetLabel !== null) return `asset:${assetLabel.toLocaleLowerCase("en")}`;
  const providerLabel = normalizeTrackedRenewalOptionalLabel(value.providerLabel);
  return providerLabel === null ? null : `provider:${providerLabel.toLocaleLowerCase("en")}`;
}

export function isGregorianDateOnly(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

export function isValidReminderLocalTime(value: string): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(value);
}

export function normalizeReminderCues(
  values: ReadonlyArray<ReminderCue>,
): ReadonlyArray<ReminderCue> {
  if (values.length < 1 || values.length > 5) {
    throw new Error("Choose between 1 and 5 reminder cues.");
  }
  const keys = new Set<string>();
  const normalized = values.map((value) => {
    if (!Number.isSafeInteger(value.offsetDays) || value.offsetDays < 0 || value.offsetDays > 730) {
      throw new Error("Reminder cues must be between 0 and 730 days before the renewal date.");
    }
    if (!isValidReminderLocalTime(value.localTime)) {
      throw new Error("Enter a valid reminder time in 24-hour HH:mm format.");
    }
    if (
      value.minutesBeforeEvent !== undefined &&
      (!Number.isSafeInteger(value.minutesBeforeEvent) ||
        value.minutesBeforeEvent < 0 ||
        value.minutesBeforeEvent > 730 * 24 * 60)
    )
      throw new Error("Choose a whole number of minutes within 730 days before the event.");
    const key = `${value.offsetDays}:${value.localTime}:${value.minutesBeforeEvent ?? "fixed"}`;
    if (keys.has(key)) throw new Error("Each reminder cue must be unique.");
    keys.add(key);
    return {
      offsetDays: value.offsetDays,
      localTime: value.localTime,
      ...(value.minutesBeforeEvent === undefined
        ? {}
        : { minutesBeforeEvent: value.minutesBeforeEvent }),
    };
  });
  return normalized.sort(
    (left, right) =>
      right.offsetDays - left.offsetDays || left.localTime.localeCompare(right.localTime),
  );
}

export function normalizeIntendedChannels(
  values: ReadonlyArray<IntendedNotificationChannel>,
): ReadonlyArray<IntendedNotificationChannel> {
  const unique = [...new Set(values)];
  if (!unique.includes("in-app")) unique.unshift("in-app");
  return intendedNotificationChannels.filter((channel) => unique.includes(channel));
}

export function normalizeParticipantIds(
  responsibleAdultId: string,
  recipientAdultIds: ReadonlyArray<string>,
): ReadonlyArray<string> {
  const responsible = responsibleAdultId.trim();
  if (responsible.length < 8) throw new Error("Choose a responsible household adult.");
  const unique = [...new Set(recipientAdultIds.map((value) => value.trim()).filter(Boolean))];
  const normalized = [responsible, ...unique.filter((adultId) => adultId !== responsible)];
  if (normalized.length > 20) throw new Error("Choose no more than 20 reminder recipients.");
  return normalized;
}

export function reminderCueUtcEpochMilliseconds(
  authoritativeDate: string,
  cue: ReminderCue,
  timeZone: string,
  eventLocalTime?: string | null,
): number {
  if (!isGregorianDateOnly(authoritativeDate)) throw new Error("Enter a valid renewal date.");
  if (!isValidReminderLocalTime(cue.localTime)) throw new Error("Enter a valid reminder time.");
  if (!Number.isSafeInteger(cue.offsetDays) || cue.offsetDays < 0 || cue.offsetDays > 730)
    throw new Error("Reminder cues must be between 0 and 730 days before the renewal date.");
  if (cue.minutesBeforeEvent !== undefined) {
    normalizeReminderCues([cue]);
    if (eventLocalTime == null || !isValidReminderLocalTime(eventLocalTime))
      throw new Error("Choose an event time for a reminder measured in minutes before it.");
    return (
      resolveReminderInstant(
        authoritativeDate,
        eventLocalTime,
        reminderZoneFormatter(timeZone),
        true,
      ).dueAtEpochMilliseconds -
      cue.minutesBeforeEvent * 60_000
    );
  }
  const calendar = new Date(authoritativeDate + "T00:00:00.000Z");
  calendar.setUTCDate(calendar.getUTCDate() - cue.offsetDays);
  return resolveReminderInstant(
    calendar.toISOString().slice(0, 10),
    cue.localTime,
    reminderZoneFormatter(timeZone),
  ).dueAtEpochMilliseconds;
}

export function dueReminderCuesWithinWindow(value: {
  eventLocalTime?: string | null;
  authoritativeDate: string;
  reminderCues: ReadonlyArray<ReminderCue>;
  timeZone: string;
  effectiveAtEpochMilliseconds: number;
  windowStartedAtEpochMilliseconds: number;
  evaluatedThroughEpochMilliseconds: number;
}): ReadonlyArray<DueReminderCue> {
  for (const epoch of [
    value.effectiveAtEpochMilliseconds,
    value.windowStartedAtEpochMilliseconds,
    value.evaluatedThroughEpochMilliseconds,
  ]) {
    if (!Number.isSafeInteger(epoch) || epoch < 0) {
      throw new Error("Use a valid occurrence evaluation window.");
    }
  }
  if (value.windowStartedAtEpochMilliseconds > value.evaluatedThroughEpochMilliseconds) {
    throw new Error("Use a valid occurrence evaluation window.");
  }
  return normalizeReminderCues(value.reminderCues)
    .map((cue) => ({
      ...cue,
      dueAtEpochMilliseconds: reminderCueUtcEpochMilliseconds(
        value.authoritativeDate,
        cue,
        value.timeZone,
        value.eventLocalTime,
      ),
    }))
    .filter(
      (cue) =>
        cue.dueAtEpochMilliseconds >= value.effectiveAtEpochMilliseconds &&
        cue.dueAtEpochMilliseconds > value.windowStartedAtEpochMilliseconds &&
        cue.dueAtEpochMilliseconds <= value.evaluatedThroughEpochMilliseconds,
    )
    .sort((left, right) => left.dueAtEpochMilliseconds - right.dueAtEpochMilliseconds);
}

export function nextOccurrenceAwarenessState(
  current: OccurrenceAwarenessState,
  action: OccurrenceAwarenessAction,
): OccurrenceAwarenessState {
  if (action === "acknowledge") {
    if (current === "aware") throw new Error("This occurrence is already marked as aware.");
    return "aware";
  }
  if (current === "not-aware") {
    throw new Error("This occurrence is not currently marked as aware.");
  }
  return "not-aware";
}
